import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fileToDataUrl } from "@/lib/image";
import { PageLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Upload, Loader2, Monitor } from "lucide-react";

export default function Settings() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/admin/settings", form)).data,
    onSuccess: (d) => {
      setForm(d);
      qc.invalidateQueries({ queryKey: ["public-settings"] });
      toast.success("Settings saved");
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isLoading || !form) return <PageLoader />;
  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Settings</p>
        <p className="text-sm text-muted-foreground mt-1">Customize the login page and the announcement banner shown to authors.</p>
      </div>

      <Tabs defaultValue="login">
        <TabsList>
          <TabsTrigger value="login" data-testid="tab-login-settings">Login Page</TabsTrigger>
          <TabsTrigger value="banner" data-testid="tab-banner-settings">Author Banner</TabsTrigger>
        </TabsList>

        <TabsContent value="login" className="mt-4">
          <Card className="p-6 lg:p-8 space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Publisher name</Label>
                <Input data-testid="set-publisher-name" value={form.publisher_name || ""} onChange={(e) => set("publisher_name", e.target.value)} placeholder="Manda Publishers" />
              </div>
              <div className="space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-3">
                  {form.login_logo && <img src={form.login_logo} alt="logo" className="h-10 w-10 object-contain rounded border border-border bg-white" />}
                  <input id="logo-file" type="file" accept="image/*" className="hidden" data-testid="set-logo-upload"
                    onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { set("login_logo", await fileToDataUrl(f, 300)); } catch (err) { toast.error(err.message); } e.target.value = ""; }} />
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => document.getElementById("logo-file").click()}>
                    <Upload className="h-4 w-4" /> {form.login_logo ? "Replace" : "Upload"}
                  </Button>
                  {form.login_logo && <Button type="button" variant="ghost" size="sm" onClick={() => set("login_logo", null)}>Remove</Button>}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Headline (left panel tagline)</Label>
              <Input data-testid="set-login-tagline" value={form.login_tagline || ""} onChange={(e) => set("login_tagline", e.target.value)} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Form title</Label>
                <Input data-testid="set-login-title" value={form.login_title || ""} onChange={(e) => set("login_title", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Form subtitle</Label>
                <Input data-testid="set-login-subtitle" value={form.login_subtitle || ""} onChange={(e) => set("login_subtitle", e.target.value)} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Background style</Label>
              <Select value={form.login_bg_type || "image"} onValueChange={(v) => set("login_bg_type", v)}>
                <SelectTrigger className="w-[200px]" data-testid="set-bg-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="gradient">Gradient</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.login_bg_type === "gradient" ? (
              <div className="space-y-2">
                <Label>CSS gradient</Label>
                <Input data-testid="set-gradient" value={form.login_gradient || ""} onChange={(e) => set("login_gradient", e.target.value)} placeholder="linear-gradient(135deg, #1f4a3a 0%, #2d6a4f 100%)" />
                <div className="h-16 rounded-lg border border-border" style={{ background: form.login_gradient }} />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Background image</Label>
                <div className="flex items-center gap-3">
                  {form.login_bg_image && <img src={form.login_bg_image} alt="bg" className="h-16 w-28 object-cover rounded-lg border border-border" />}
                  <input id="login-bg-file" type="file" accept="image/*" className="hidden" data-testid="set-bg-upload"
                    onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { set("login_bg_image", await fileToDataUrl(f, 1200)); } catch (err) { toast.error(err.message); } e.target.value = ""; }} />
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => document.getElementById("login-bg-file").click()}>
                    <Upload className="h-4 w-4" /> Upload
                  </Button>
                </div>
                <Input data-testid="set-bg-url" value={form.login_bg_image || ""} onChange={(e) => set("login_bg_image", e.target.value)} placeholder="...or paste an image URL" />
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="banner" className="mt-4">
          <Card className="p-6 lg:p-8 space-y-5">
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div>
                <Label>Show banner to authors</Label>
                <p className="text-xs text-muted-foreground">A thin strip at the top of the author panel.</p>
              </div>
              <Switch data-testid="set-banner-enabled" checked={!!form.banner_enabled} onCheckedChange={(v) => set("banner_enabled", v)} />
            </div>
            <div className="space-y-2">
              <Label>Banner text</Label>
              <Textarea data-testid="set-banner-text" value={form.banner_text || ""} onChange={(e) => set("banner_text", e.target.value)} placeholder="e.g. June royalties are now live!" className="min-h-[60px]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Background</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.banner_bg || "#1f4a3a"} onChange={(e) => set("banner_bg", e.target.value)} className="h-9 w-12 rounded border border-border" data-testid="set-banner-bg" />
                  <Input value={form.banner_bg || ""} onChange={(e) => set("banner_bg", e.target.value)} className="flex-1" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Text colour</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={form.banner_text_color || "#ffffff"} onChange={(e) => set("banner_text_color", e.target.value)} className="h-9 w-12 rounded border border-border" data-testid="set-banner-text-color" />
                  <Input value={form.banner_text_color || ""} onChange={(e) => set("banner_text_color", e.target.value)} className="flex-1" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Link (optional)</Label>
                <Input data-testid="set-banner-link" value={form.banner_link || ""} onChange={(e) => set("banner_link", e.target.value)} placeholder="https://..." />
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1.5"><Monitor className="h-3.5 w-3.5" /> Preview</p>
              <div className="w-full flex items-center justify-center text-sm font-medium rounded-md" style={{ background: form.banner_bg || "#1f4a3a", color: form.banner_text_color || "#fff", height: 30 }}>
                {form.banner_text || "Your banner text"}
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Button data-testid="save-settings" onClick={() => save.mutate()} disabled={save.isPending} className="gap-2">
        {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
      </Button>
    </div>
  );
}
